/* ============================================================
   Wire ↔ app mapping — converts the server record shapes (api/types.ts) into the
   app's local types (src/types.ts) and back for create bodies. The two models
   differ in small, deliberate ways:

     • emoji     habits only — server `string | null` ↔ app Habit `string`
                                            ('' when none)
     • lastTouched  server ISO string    →  app epoch ms (orders the backlog)
     • pins      server list of records  →  app `pinnedByDay` map (day → itemId)
     • projects     carried through nearly as-is; ARCHIVED projects are KEPT (unlike
                 archived habits) because their tasks still need colouring and
                 the project index shows them under "Archived"
     • tags      name only — a tag has no hue and no archived state, so there is
                 nothing else the UI needs

   A full `/v1/sync` returns tombstoned (deletedAt) and archived rows so deletes
   propagate across devices; the hydrate helpers drop them, since the app's local
   AppState has no place for a dead row (a soft-delete on another device simply
   becomes absent here).
   ============================================================ */
import { DAILY_SCHEDULE } from '@habishine/shared';
import type { AppState, DayCheckin, Habit, Project, Tag, Task } from '../types';
import type {
  ServerCheckin,
  ServerHabit,
  ServerProject,
  ServerPin,
  ServerTag,
  ServerTask,
  SyncResponse,
} from '../api/types';

/** ISO timestamp → epoch ms; 0 for a missing/unparseable value (sorts oldest). */
function epoch(iso: string | null | undefined): number {
  if (!iso) return 0;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? 0 : ms;
}

/** Server task → app Task. `lastTouched` becomes epoch ms;
   `doneAt`/`skippedAt`/`skipped` carry over as-is. */
export function toTask(s: ServerTask): Task {
  return {
    id: s.id,
    name: s.name,
    hue: s.hue,
    done: s.done,
    subSteps: s.subSteps.map((step) => ({ t: step.t, done: step.done })),
    // `?? null` because a backend that predates projects omits the field; null is
    // the same thing it means anyway — unfiled.
    projectId: s.projectId ?? null,
    // `?? []` because a backend that predates tags omits the field; an untagged
    // task is what a missing one means anyway, and the empty array keeps every
    // reader from having to nil-check.
    tagIds: s.tagIds ?? [],
    scheduledFor: s.scheduledFor,
    // `?? null` because a backend that predates task times omits the field
    // entirely; undefined would read as "all-day" anyway, but null keeps the
    // local shape uniform for the selectors that sort on it.
    remindAt: s.remindAt ?? null,
    // a backend without the column leaves every task on 0; the list then falls
    // through to its secondary sort, which is exactly the old behaviour
    sortOrder: s.sortOrder ?? 0,
    doneAt: s.doneAt,
    skipped: s.skipped,
    skippedAt: s.skippedAt,
    lastTouched: epoch(s.lastTouched),
    rrule: s.rrule,
  };
}

/** Server project → app Project. Only the fields the UI needs — `userId` and the
   timestamps stay on the wire, since a project carries no derived state. */
export function toProject(s: ServerProject): Project {
  return { id: s.id, name: s.name, hue: s.hue, archivedAt: s.archivedAt ?? null };
}

/** Server tag → app Tag. Just the name: a tag owns no colour and no lifecycle
   beyond existing (see docs/tags.md). */
export function toTag(s: ServerTag): Tag {
  // A backend that predates the hue column omits it. Fall back rather than
  // rendering a blank swatch — the migration spreads existing rows across the
  // palette, so this only covers the window before it runs.
  return { id: s.id, name: s.name, hue: s.hue ?? 'teal' };
}

/** Server habit (definition + derived state) → app Habit. `emoji` null becomes
   '' (the app treats a habit's emoji as a required string).

   EVERY derived field falls back, because a backend that predates habit
   schedules omits all of them — and a missing number is worse than a wrong one
   here: it renders as the literal string "undefined" the moment any copy
   interpolates it. The fallbacks describe a fresh daily habit (nothing kept,
   nothing to protect), which is the honest reading of "the server told us
   nothing". The app re-derives all of it for the *viewed* day anyway
   (store/logic.ts `habitForDay`); these values only cover the moment between a
   write's response and the next re-derive.

   `startsOn` matters most: it's an INPUT to that re-derivation, not just a
   display field, so an undefined one silently corrupts every consistency number
   computed from it. It falls back to the creation date — the same answer
   migration 0009 backfills server-side. */
export function toHabit(s: ServerHabit): Habit {
  return {
    id: s.id,
    name: s.name,
    emoji: s.emoji ?? '',
    hue: s.hue,
    target: s.target,
    schedule: s.schedule ?? DAILY_SCHEDULE,
    startsOn: s.startsOn ?? s.createdAt.slice(0, 10),
    // a backend without the column leaves every habit on 0; the list then falls
    // back to server order, which is exactly the old behaviour
    sortOrder: s.sortOrder ?? 0,
    progress: s.progress ?? 0,
    streak: s.streak ?? 0,
    best: s.best ?? 0,
    done: s.done ?? false,
    dueToday: s.dueToday ?? true,
    period: s.period ?? null,
    rate: s.rate ?? null,
    kept: s.kept ?? 0,
    expected: s.expected ?? 0,
    unit: s.unit ?? 'day',
    atRisk: s.atRisk ?? false,
  };
}

/** Build the whole local AppState from a full `/v1/sync` response. Tombstoned
   (deletedAt) and archived habits are dropped — the app keeps only live rows.
   Pins that survive point at an itemId per day; the selectors defensively hide a
   pin whose target is gone or resolved, so no filtering is needed here. */
export function toAppState(res: SyncResponse, cap: number): AppState {
  const tasks = res.tasks.filter((t) => t.deletedAt === null).map(toTask);
  // Tombstoned projects go; ARCHIVED ones stay — see the header note. Tolerates a
  // missing collection (a backend that predates projects) so a partial response
  // still hydrates tasks and habits instead of wiping the UI.
  const projects = (res.projects ?? []).filter((l) => l.deletedAt === null).map(toProject);
  // Same tolerance for a backend that predates tags. Tombstones go; there is no
  // archived state to keep, so the live set is all of it.
  const tags = (res.tags ?? []).filter((t) => t.deletedAt === null).map(toTag);
  const habits = res.habits
    .filter((h) => h.deletedAt === null && h.archivedAt === null)
    .map(toHabit)
    // The server already orders by rank, but sorting here too means a backend
    // that predates the column (every habit on 0) keeps its response order
    // rather than being reshuffled — and it is THIS function that used to throw
    // a local habit reorder away.
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const pinnedByDay = toPinnedByDay(res.pins);
  const checkins = toCheckins(res.checkins);
  return { cap, tasks, projects, tags, habits, pinnedByDay, checkins };
}

/** Server check-in project → nested map habitId → day → { progress, completed }.
   Tolerates a missing project (an older backend that predates the `checkins` field)
   so a partial response still hydrates tasks/habits instead of wiping the UI. */
export function toCheckins(
  project: ServerCheckin[] | undefined | null,
): Record<string, Record<string, DayCheckin>> {
  const map: Record<string, Record<string, DayCheckin>> = {};
  for (const c of project ?? []) {
    (map[c.habitId] ??= {})[c.day] = { progress: c.progress, completed: c.completed };
  }
  return map;
}

/** Server pin project → `pinnedByDay` map (day → itemId). Last write per day wins
   (the project is already one row per day, so order is immaterial). */
export function toPinnedByDay(pins: ServerPin[]): Record<string, string | null> {
  const map: Record<string, string | null> = {};
  for (const p of pins) map[p.day] = p.itemId;
  return map;
}
